import { useState } from 'react';
import { useGT } from 'gt-react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ExpandableIdSearchInputProps = {
  /** Raw comma/space-separated id list (controlled). */
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Singular noun for the count chip, e.g. "PO #", "TO #". Defaults to "ID". */
  idLabel?: string;
  className?: string;
};

/** Count of distinct, non-empty tokens — mirrors the backend's comma/space split. */
function countTokens(value: string): number {
  return value
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean).length;
}

/**
 * Compact id-search filter that pops open a larger floating textarea on click,
 * so users can paste and review tens or hundreds of ids at once. The trigger
 * stays small in the toolbar and shows what's currently filtered.
 */
export function ExpandableIdSearchInput({
  value,
  onChange,
  placeholder,
  idLabel = 'ID',
  className,
}: ExpandableIdSearchInputProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);

  const tokenCount = countTokens(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-8 w-[200px] items-center justify-between gap-1 rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            className,
          )}
        >
          <span
            className={cn(
              'truncate',
              tokenCount === 0 && 'text-muted-foreground',
            )}
          >
            {tokenCount === 0 ? placeholder : value}
          </span>
          {tokenCount > 0 && (
            <span className="shrink-0 rounded-sm bg-muted px-1 text-[11px] text-muted-foreground tabular-nums">
              {tokenCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 space-y-2">
        <Textarea
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="h-48 resize-none font-mono text-sm"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {gt('{count} {label}s', {
              count: tokenCount,
              label: idLabel,
              context: 'count of pasted ids in the search/filter box',
            })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={tokenCount === 0}
            onClick={() => onChange('')}
          >
            {gt('Clear', { context: 'clear the id search/filter box' })}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
